import React, {useContext, useEffect} from "react";
import {useRecoilState, useSetRecoilState} from "recoil";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";
import {Stack, Typography} from "@mui/material";
import Chip from "@mui/material/Chip";
import {HealthAndSafety} from "@mui/icons-material";
import {
	checkOutInModeState,
	checkOutInState,
	checkOutInStepState,
	raFetchedDataState
} from "./state";
import VehicleStatus from "./vehicle-status/VehicleStatus";
import CheckDamage from "./check-damage/CheckDamage";
import CheckEquipment from "./check-equipment/CheckEquipment";
import CheckoutSummary from "./summary/Summary";
import Signature from "./signature/Signature";
import {fetchRaCheckOutIn, fetchSearchRaByRaIdCode} from "./services/http.services";
import { setSnackbar } from "../../../util/state/globalAction";
import { snackbarTypes } from "../../../util/state/globalVariables";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import SectionHeader from "../../../util/component/SectionHeader";
import ContentBody from "../common/content-body/ContentBody";
import CustomLoader from "../../../util/component/CustomLoader";
import {getCheckOutInSessionStorage, saveCheckOutInSessionStorage} from "./services/sessionStorageManager";
import {AxiosError} from "axios";
import {isMobile} from "react-device-detect";
import NotAllowedDialog from "../../authentication/component/NotAllowedDialog";
import {recoveryRaModeState} from "../create-ra/state";

const RecoveryCheckOut = () => {
	const {t} = useTranslation(['network', 'translation']);
	const {dispatchGlobalData} = useContext(GlobalContext);
	const navigate = useNavigate();
	const params = useParams();

	const [checkOutIn, setCheckOutIn] = useRecoilState(checkOutInState);
	const [checkStep, setCheckStep] = useRecoilState(checkOutInStepState);
	const setCheckOutInMode = useSetRecoilState(checkOutInModeState);
	const [raFetchedData, setRaFetchedData] = useRecoilState(raFetchedDataState);
	const [recoveryMode, setRecoveryMode] = useRecoilState(recoveryRaModeState);

	const onSuccessFetchRaCheckIn = async (response: any) => {
		if (!!response) {
			setRaFetchedData(response);
			setCheckOutInMode('out');
			setCheckStep({
				active: 0,
				numSteps: 5
			});
		}
	}

	const onErrorFetchRaCheckIn = (error: any) => {
		dispatchGlobalData({
			type: setSnackbar,
			payload: {
				snackbar: {
					message: t('translation:error.tryLater'),
					type: snackbarTypes.error,
					show: true,
					autoClose: true
				}
			}
		});
	}

	const viewErrorMessage = (message: string) => {
		dispatchGlobalData({
			type: 'SET_SNACKBAR',
			payload: {
				snackbar: {
					message: t(message),
					type: snackbarTypes.error,
					show: true,
					autoClose: true,
				}
			}
		});
	}

	const {isFetching, refetch} = useQuery(['raCheckIn', {id: params.id}], () => fetchRaCheckOutIn({id: params.id}), {
		enabled: false,
		onSuccess: (data) => onSuccessFetchRaCheckIn(data),
		onError: (error) => onErrorFetchRaCheckIn(error)
	})

	const fetchSearchRaByRaIdCodeQuery = useQuery(
		['fetchSearchRaByRaIdCode', {id: params.id}],
		() => fetchSearchRaByRaIdCode({id: params.id, type: 'check-out'}),
		{
		enabled: false,
		onSuccess: (response: any) => {
			if (response.executed) {
				viewErrorMessage('raCheckOutIn.raVehicleIdentification.message.error.executed');
				navigate('/');
			} else {
				checkSessionData();
			}
		},
		onError: (error: AxiosError) => {
			let mess = '';
			// @ts-ignore
			if (error.response?.status === 404 && error.response?.data?.errorDetails === "\"ERROR_ID_RA_NOT_FOUND\"") {
				mess = "common.error.notExistingRa"
			}
			// @ts-ignore
			else if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"ERROR_INCONSISTENT_RA_STATE\""){
				mess = "common.error.errorInconsistentRaState"
			}// @ts-ignore
			else if (error.response?.status === 400 && error.response?.data?.errorDetails === "\"ERROR_RA_TO_RECONCILE\""){
				mess = "common.error.errorRaToReconcile"
			}
			else{
				mess = 'translation:error.tryLater';
			}
			viewErrorMessage(mess);
		}
	})

	useEffect(() => {
		if (checkStep.active !== null) {
			saveCheckOutInSessionStorage(checkOutIn);
		}
	}, [checkStep])

	const checkSessionData = async () => {
		let sessionStrData: any = getCheckOutInSessionStorage();
		if (!!sessionStrData && params.id === sessionStrData['raFetchedData'].idRentalAgreement && sessionStrData.type === 'out') {
			await setCheckOutIn(sessionStrData);
			await setCheckStep({
				active: 0,// goto step 0 for reinsert img
				numSteps: 5
			});
		}
		else {
			if (!!raFetchedData) {
				setCheckOutInMode('out');
				setCheckStep({
					active: 0,
					numSteps: 5
				});
			} else {
				refetch();
			}
		}
	}

	useEffect(() => {
		if (isMobile) {
			fetchSearchRaByRaIdCodeQuery.refetch();
		}

		setRecoveryMode(true);
	}, [])

	return (
		<>
			<SectionHeader title={
				<Stack direction="row" alignItems="center" columnGap={3}>
					<Stack direction="row" alignItems="end" columnGap={1}>
						<Typography variant="bodyLarge">{t("raCheckOutIn.out.header.title")}</Typography>
						{
							recoveryMode &&
							<Chip label={t("raCheckOutIn.out.header.title")} color={'error'} icon={<HealthAndSafety />}/>
						}
					</Stack>
				</Stack>
			} />
			{
				!isMobile ?
					<NotAllowedDialog title={t("raCheckOutIn.label.operationNotAllowed")} message={t("raCheckOutIn.label.switchToMobile")}/>:
					<ContentBody>
						{  checkStep.active === 0 && <VehicleStatus /> }
						{  checkStep.active === 1 && <CheckDamage /> }
						{  checkStep.active === 2 && <CheckEquipment /> }
						{  checkStep.active === 3 && <CheckoutSummary /> }
						{  checkStep.active === 4 && <Signature /> }
					</ContentBody>
			}

			<CustomLoader loading={isFetching}/>
		</>
	)
}

export default  RecoveryCheckOut;
